import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface OnboardingStepProps {
  stepNumber: number;
  title: string;
  subtitle: string;
  isCompleted: boolean;
  isHighlighted: boolean;
  buttonText?: string;
  progressText?: string; // e.g., "3/5" for favorites
  onClick: () => void;
}

const OnboardingStep = ({
  stepNumber,
  title,
  subtitle,
  isCompleted,
  isHighlighted,
  buttonText,
  progressText,
  onClick,
}: OnboardingStepProps) => {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 p-4 rounded-xl transition-all cursor-pointer",
        isCompleted
          ? "bg-muted/50 opacity-75"
          : isHighlighted
            ? "bg-accent/10 border-2 border-accent/30"
            : "bg-background hover:bg-muted/30",
        !isCompleted && "hover:shadow-sm"
      )}
      onClick={onClick}
    >
      {/* Step indicator */}
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all",
            isCompleted
              ? "bg-green-500 text-white"
              : isHighlighted
                ? "bg-accent text-accent-foreground"
                : "bg-muted text-muted-foreground"
          )}
        >
          {isCompleted ? (
            <Check className="h-4 w-4" />
          ) : (
            stepNumber
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              "text-sm font-medium leading-tight",
              isCompleted && "line-through text-muted-foreground"
            )}
          >
            {title}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Action area */}
      {!isCompleted && (
        <div className="mt-1 ml-11">
          {progressText ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-accent">
                {progressText}
              </span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => {
                  const current = parseInt(progressText.split("/")[0]) || 0;
                  return (
                    <div
                      key={i}
                      className={cn(
                        "w-1.5 h-1.5 rounded-full",
                        i <= current ? "bg-accent" : "bg-muted"
                      )}
                    />
                  );
                })}
              </div>
            </div>
          ) : buttonText ? (
            <Button
              size="sm"
              variant={isHighlighted ? "default" : "outline"}
              className={cn(
                "h-7 text-xs px-3",
                isHighlighted && "bg-accent hover:bg-accent/90 text-accent-foreground"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              {buttonText}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default OnboardingStep;
